import React, { useRef } from 'react';
import html2pdf from 'html2pdf.js';
import './FicheAdhesion.css';
import '../pages/ListeMontantsPrint.css';

const API_BASE_URL = process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:5000';

const FicheAdhesion = ({ membre, onClose }) => {
  const printRef = useRef(null);

  const handlePrint = () => {
    setTimeout(() => {
      window.print();
    }, 100);
  };

  const handleDownloadPDF = async () => {
    if (!printRef.current) return;

    try {
      const opt = {
        margin: [10, 10, 10, 10],
        filename: `Fiche_Adhesion_${membre.numero_matricule || 'MNE'}_${new Date().toISOString().split('T')[0]}.pdf`,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { 
          scale: 2,
          useCORS: true,
          logging: false,
          backgroundColor: '#ffffff',
          width: printRef.current.scrollWidth,
          height: printRef.current.scrollHeight,
          windowWidth: printRef.current.scrollWidth,
          allowTaint: true
        },
        jsPDF: { 
          unit: 'mm', 
          format: 'a4', 
          orientation: 'portrait'
        },
        pagebreak: { mode: ['avoid-all', 'css', 'legacy'] }
      };

      await html2pdf().set(opt).from(printRef.current).save();
    } catch (error) {
      console.error('Erreur lors de la génération du PDF:', error);
      alert('Erreur lors de la génération du PDF. Veuillez réessayer.');
    }
  };

  if (!membre) return null;

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', { 
      day: '2-digit', 
      month: '2-digit', 
      year: 'numeric' 
    });
  };

  return (
    <div className="fiche-modal-overlay" onClick={onClose}>
      <div className="fiche-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="fiche-header">
          <h2>FICHE D'ADHÉSION - MUTU NDE ESPOIR ASBL</h2>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>

        <div className="fiche-container" ref={printRef}>
          <div className="print-content">
            {/* En-tête avec logo - Style cohérent avec les autres documents */}
            <div className="print-header">
              <div className="header-content-wrapper">
                <div className="header-logo">
                  <img src="/logo.png" alt="Logo" className="logo-img" />
                </div>
                <div className="header-text">
                  <h1>MUTU NDE ESPOIR ASBL</h1>
                  <h2>FICHE D'ADHÉSION</h2>
                  <div className="header-info">
                    <p className="print-date">
                      <strong>Date d'édition:</strong> {new Date().toLocaleDateString('fr-FR', { 
                        weekday: 'long', 
                        year: 'numeric', 
                        month: 'long', 
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="fiche-adhesion">

            {/* Informations du membre */}
            <div className="fiche-body">
              {/* Section gauche - Informations personnelles */}
              <div className="fiche-section fiche-section-left">
                <h3>INFORMATIONS PERSONNELLES</h3>
                
                <div className="fiche-field">
                  <span className="fiche-label">Numéro Matricule :</span>
                  <span className="fiche-value">{membre.numero_matricule}</span>
                </div>

                <div className="fiche-field">
                  <span className="fiche-label">Nom :</span>
                  <span className="fiche-value">{membre.nom}</span>
                </div>

                {membre.postnom && (
                  <div className="fiche-field">
                    <span className="fiche-label">Postnom :</span>
                    <span className="fiche-value">{membre.postnom}</span>
                  </div>
                )}

                <div className="fiche-field">
                  <span className="fiche-label">Prénom :</span>
                  <span className="fiche-value">{membre.prenom}</span>
                </div>

                <div className="fiche-field">
                  <span className="fiche-label">Date de naissance :</span>
                  <span className="fiche-value">{formatDate(membre.date_naissance)}</span>
                </div>

                {membre.lieu_naissance && (
                  <div className="fiche-field">
                    <span className="fiche-label">Lieu de naissance :</span>
                    <span className="fiche-value">{membre.lieu_naissance}</span>
                  </div>
                )}

                {membre.telephone && (
                  <div className="fiche-field">
                    <span className="fiche-label">Téléphone :</span>
                    <span className="fiche-value">{membre.telephone}</span>
                  </div>
                )}

                {membre.profession && (
                  <div className="fiche-field">
                    <span className="fiche-label">Profession :</span>
                    <span className="fiche-value">{membre.profession}</span>
                  </div>
                )}

                {membre.association && (
                  <div className="fiche-field">
                    <span className="fiche-label">Association :</span>
                    <span className="fiche-value">{membre.association}</span>
                  </div>
                )}
              </div>

              {/* Section droite - Adresse et famille */}
              <div className="fiche-section fiche-section-right">
                <h3>ADRESSE ET FAMILLE</h3>

                {membre.quartier && (
                  <div className="fiche-field">
                    <span className="fiche-label">Quartier :</span>
                    <span className="fiche-value">{membre.quartier}</span>
                  </div>
                )}

                {membre.commune && (
                  <div className="fiche-field">
                    <span className="fiche-label">Commune :</span>
                    <span className="fiche-value">{membre.commune}</span>
                  </div>
                )}

                {membre.nom_epouse && (
                  <div className="fiche-field">
                    <span className="fiche-label">Nom de l'épouse/époux :</span>
                    <span className="fiche-value">{membre.nom_epouse}</span>
                  </div>
                )}

                <div className="fiche-field">
                  <span className="fiche-label">Nombre d'enfants (-16 ans) :</span>
                  <span className="fiche-value">{membre.nombre_enfants || 0}</span>
                </div>

                {membre.noms_enfants && (
                  <div className="fiche-field">
                    <span className="fiche-label">Noms des enfants :</span>
                    <span className="fiche-value">{membre.noms_enfants}</span>
                  </div>
                )}

                <div className="fiche-field">
                  <span className="fiche-label">Date d'adhésion :</span>
                  <span className="fiche-value fiche-value-highlight">{formatDate(membre.date_adhesion)}</span>
                </div>

                {membre.nom_recolteur && (
                  <div className="fiche-field">
                    <span className="fiche-label">Récolteur :</span>
                    <span className="fiche-value">{membre.nom_recolteur}</span>
                  </div>
                )}

                {membre.code_barres && (
                  <div className="fiche-field">
                    <span className="fiche-label">Code-barres :</span>
                    <span className="fiche-value fiche-code-barres">{membre.code_barres}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Section photos et signature */}
            <div className="fiche-photos-section">
              <div className="fiche-photo-item">
                <h4>Photo du membre</h4>
                {membre.photo ? (
                  <img 
                    src={`${API_BASE_URL}${membre.photo}`} 
                    alt="Membre"
                    className="fiche-photo"
                  />
                ) : (
                  <div className="fiche-photo-placeholder">Aucune photo</div>
                )}
              </div>

              <div className="fiche-photo-item">
                <h4>Signature</h4>
                {membre.photo_signature ? (
                  <img 
                    src={`${API_BASE_URL}${membre.photo_signature}`} 
                    alt="Signature"
                    className="fiche-signature"
                  />
                ) : (
                  <div className="fiche-photo-placeholder">Aucune signature</div>
                )}
              </div>
            </div>

            {/* Footer - Style cohérent avec les autres documents */}
            <div className="print-footer">
              <p>Cette fiche d'adhésion est générée automatiquement par le système de gestion MUTU NDE ESPOIR ASBL</p>
            </div>
            </div>
          </div>
        </div>

        <div className="fiche-actions">
          <button className="btn btn-primary" onClick={handlePrint}>
            🖨️ Imprimer
          </button>
          <button className="btn btn-success" onClick={handleDownloadPDF}>
            📥 Télécharger PDF
          </button>
          <button className="btn btn-secondary" onClick={onClose}>
            Fermer
          </button>
        </div>
      </div>
    </div>
  );
};

export default FicheAdhesion;


